import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import GenreChip from '../components/GenreChip';
import AdSlot from '../components/AdSlot';
import { getEvent } from '../api/client';
import styles from './EventDetail.module.css';

function formatTime(t: string | null) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: event, isLoading, isError } = useQuery({
    queryKey: ['event', id],
    queryFn: () => getEvent(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className={styles.loading}>Loading…</div>;
  if (isError || !event) return <div className={styles.error}>Event not found.</div>;

  const date = new Date(`${event.eventDate}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(
    `${event.venue.name}, ${event.venue.address}, ${event.venue.city} ${event.venue.state}`
  )}`;

  const title = `${event.artist.name} at ${event.venue.name} — ${date} | ConcertFinder`;
  document.title = title;

  return (
    <div className={`container ${styles.page}`}>
      {event.status === 'cancelled' && <div className={styles.cancelBanner}>CANCELLED</div>}

      <div className={styles.header}>
        <p className={styles.date}>{date}</p>
        <div className={styles.titleRow}>
          <h1 className={styles.artist}>{event.artist.name}</h1>
          <GenreChip genre={event.artist.genre} size="md" />
        </div>
        {event.startTime && (
          <p className={styles.time}>{formatTime(event.startTime)}{event.endTime ? ` – ${formatTime(event.endTime)}` : ''}</p>
        )}
      </div>

      <hr className={styles.divider} />

      <div className={styles.venueSection}>
        <p className={styles.venueIcon}>📍</p>
        <div>
          <p className={styles.venueName}>{event.venue.name}</p>
          <p className={styles.venueAddress}>
            {event.venue.address}, {event.venue.city} {event.venue.state} {event.venue.zip}
          </p>
          <div className={styles.venueLinks}>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">Get Directions</a>
            {event.venue.website && (
              <a href={event.venue.website} target="_blank" rel="noopener noreferrer">Venue Website</a>
            )}
          </div>
        </div>
      </div>

      {(event.doorsTime || event.startTime || event.ageRestriction) && (
        <div className={styles.metaRow}>
          {event.doorsTime && <span>Doors {formatTime(event.doorsTime)}</span>}
          {event.startTime && <span>Show {formatTime(event.startTime)}</span>}
          {event.endTime && !event.doorsTime && !event.startTime && <span>Until {formatTime(event.endTime)}</span>}
          {event.ageRestriction && <span>{event.ageRestriction}</span>}
        </div>
      )}

      <div className={styles.ticketSection}>
        <p className={styles.ticketIcon}>🎟</p>
        <div>
          {event.ticketUrl ? (
            <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer" className={styles.ticketLink}>
              Buy Tickets →
            </a>
          ) : (
            <span className={styles.noTicket}>
              {event.coverCharge === false
                ? 'Free · No tickets required'
                : <>Contact venue for tickets{event.venue.phone ? ` · ${event.venue.phone}` : ''}</>
              }
            </span>
          )}
          {event.ticketPrice && <span className={styles.price}> · {event.ticketPrice}</span>}
        </div>
      </div>

      {event.description && (
        <p className={styles.description}>{event.description}</p>
      )}

      {event.supportingActs && (
        <p className={styles.supporting}>Supporting acts: {event.supportingActs}</p>
      )}

      {event.artist.instagramUrl && (
        <a
          href={event.artist.instagramUrl.startsWith('http') ? event.artist.instagramUrl : `https://instagram.com/${event.artist.instagramUrl.replace('@', '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.instagramLink}
        >
          Instagram →
        </a>
      )}

      <AdSlot type="inline" className={styles.ad} />

      <Link to={-1 as any} className={styles.backLink}>← Back to results</Link>
    </div>
  );
}
